import { useEffect, useMemo, useState } from "react";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { ChevronDown } from "lucide-react";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
  ChartContainer,
  ChartTooltip,
} from "@/components/ui/chart";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  fetchWorkspaceTaskPerformanceTimeseries,
  type TaskPerformanceTimeseriesPoint,
} from "@/lib/task-metrics";
import {
  buildMemberAreaChartConfig,
  memberAreaColor,
  memberSeriesKey,
} from "@/components/tasks/task-performance-area-chart-config";

function addDaysISO(dateIso: string, days: number): string {
  const d = new Date(dateIso + "T12:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function isValidMetricDateRange(from: string, to: string): boolean {
  return ISO_DATE_RE.test(from) && ISO_DATE_RE.test(to) && from <= to;
}

function eachDayISO(from: string, to: string): string[] {
  if (!isValidMetricDateRange(from, to)) return [];

  const days: string[] = [];
  let current = from;
  while (current <= to) {
    days.push(current);
    current = addDaysISO(current, 1);
  }
  return days;
}

type ChartRow = Record<string, string | number>;

interface MemberMeta {
  id: string;
  name: string;
  dataKey: string;
  colorIndex: number;
}

interface TaskPerformanceMemberAreaChartProps {
  workspaceId: string;
  from: string;
  to: string;
  memberNames: { id: string; name: string }[];
  loading?: boolean;
  projectId?: string | null;
}

function AreaTooltip({
  active,
  payload,
  label,
  members,
  metric,
}: {
  active?: boolean;
  payload?: Array<{ dataKey?: string; value?: number; color?: string; name?: string }>;
  label?: string;
  members: MemberMeta[];
  metric: "completed" | "completed_late";
}) {
  if (!active || !payload?.length) return null;

  const entries = payload
    .filter((p) => (p.value ?? 0) > 0)
    .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));

  const total = entries.reduce((sum, p) => sum + (p.value ?? 0), 0);

  return (
    <div className="rounded-lg border bg-popover text-popover-foreground shadow-md p-3 min-w-[180px]">
      <p className="text-sm font-medium mb-2">{label}</p>
      <div className="space-y-1.5">
        {entries.map((p) => {
          const member = members.find((m) => m.dataKey === p.dataKey);
          return (
            <div
              key={p.dataKey}
              className="flex items-center justify-between gap-3 text-sm"
            >
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className="h-2.5 w-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: p.color }}
                />
                <span className="truncate">{member?.name ?? p.name}</span>
              </div>
              <span className="font-medium tabular-nums shrink-0">{p.value}</span>
            </div>
          );
        })}
      </div>
      <p className="text-xs text-muted-foreground mt-2 pt-2 border-t border-border">
        Total: {total}{" "}
        {metric === "completed_late" ? "em atraso" : "concluída(s)"}
      </p>
    </div>
  );
}

export function TaskPerformanceMemberAreaChart({
  workspaceId,
  from,
  to,
  memberNames,
  loading: parentLoading,
  projectId = null,
}: TaskPerformanceMemberAreaChartProps) {
  const [timeseries, setTimeseries] = useState<TaskPerformanceTimeseriesPoint[]>([]);
  const [fetching, setFetching] = useState(false);
  const [metric, setMetric] = useState<"completed" | "completed_late">("completed");
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(new Set());
  const [membersOpen, setMembersOpen] = useState(false);

  const members = useMemo<MemberMeta[]>(
    () =>
      memberNames.map((m, index) => ({
        id: m.id,
        name: m.name,
        dataKey: memberSeriesKey(m.id),
        colorIndex: index,
      })),
    [memberNames],
  );

  useEffect(() => {
    setSelectedMemberIds(new Set(members.map((m) => m.id)));
  }, [members]);

  useEffect(() => {
    if (!workspaceId || !isValidMetricDateRange(from, to)) return;
    let cancelled = false;

    const load = async () => {
      setFetching(true);
      try {
        const data = await fetchWorkspaceTaskPerformanceTimeseries(
          workspaceId,
          from,
          to,
          "day",
          projectId,
        );
        if (!cancelled) setTimeseries(data);
      } catch (err) {
        console.error("TaskPerformanceMemberAreaChart:", err);
        if (!cancelled) setTimeseries([]);
      } finally {
        if (!cancelled) setFetching(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, from, to, projectId]);

  const chartData = useMemo(() => {
    if (!isValidMetricDateRange(from, to)) return [];

    const days = eachDayISO(from, to);
    const byBucketMember = new Map<string, TaskPerformanceTimeseriesPoint>();
    for (const point of timeseries) {
      byBucketMember.set(`${point.bucket_date}:${point.member_id}`, point);
    }

    return days.map((day) => {
      const row: ChartRow = {
        date: format(parseISO(day), "dd/MM", { locale: ptBR }),
        dateIso: day,
      };
      for (const member of members) {
        const point = byBucketMember.get(`${day}:${member.id}`);
        row[member.dataKey] =
          metric === "completed_late"
            ? (point?.completed_late ?? 0)
            : (point?.completed ?? 0);
      }
      return row;
    });
  }, [from, to, timeseries, members, metric]);

  const visibleMembers = members.filter((m) => selectedMemberIds.has(m.id));
  const chartConfig = useMemo(
    () => buildMemberAreaChartConfig(visibleMembers.map((m) => ({ id: m.id, name: m.name }))),
    [visibleMembers],
  );

  const hasActivity = chartData.some((row) =>
    visibleMembers.some((m) => Number(row[m.dataKey] ?? 0) > 0),
  );

  const maxValue = Math.max(
    0,
    ...chartData.map((row) =>
      visibleMembers.reduce((sum, m) => sum + Number(row[m.dataKey] ?? 0), 0),
    ),
  );

  const loading = parentLoading || fetching;

  const selectedCount = selectedMemberIds.size;
  const memberFilterLabel =
    selectedCount === members.length
      ? "Membros"
      : `Filtrar membros (${selectedCount}/${members.length})`;

  const toggleMember = (memberId: string, checked: boolean) => {
    setSelectedMemberIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(memberId);
      } else if (next.size > 1) {
        next.delete(memberId);
      }
      return next;
    });
  };

  const selectAllMembers = () => {
    setSelectedMemberIds(new Set(members.map((m) => m.id)));
  };

  const clearMembers = () => {
    setSelectedMemberIds(new Set());
  };

  if (!isValidMetricDateRange(from, to)) {
    return null;
  }

  if (loading) {
    return (
      <div className="h-[320px] flex items-center justify-center rounded-lg border bg-muted/10">
        <p className="text-sm text-muted-foreground">Carregando gráfico...</p>
      </div>
    );
  }

  if (members.length === 0) {
    return (
      <div className="h-[320px] flex items-center justify-center rounded-lg border bg-muted/10">
        <p className="text-sm text-muted-foreground">Sem dados para o período selecionado</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border p-3 sm:p-4 space-y-3 bg-muted/10">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h4 className="text-sm font-medium">Conclusões ao longo do tempo</h4>
          <p className="text-xs text-muted-foreground mt-0.5">
            Por membro — selecione quem exibir no gráfico
          </p>
        </div>
        <div className="flex rounded-lg border p-0.5 bg-background shrink-0">
          <Button
            type="button"
            size="sm"
            variant={metric === "completed" ? "default" : "ghost"}
            className="h-7 text-xs"
            onClick={() => setMetric("completed")}
          >
            Concluídas
          </Button>
          <Button
            type="button"
            size="sm"
            variant={metric === "completed_late" ? "default" : "ghost"}
            className="h-7 text-xs"
            onClick={() => setMetric("completed_late")}
          >
            Em atraso
          </Button>
        </div>
      </div>

      <Popover open={membersOpen} onOpenChange={setMembersOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 text-xs font-normal"
          >
            {memberFilterLabel}
            <ChevronDown className="h-3.5 w-3.5 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-2" align="start">
          <div className="flex items-center justify-between px-2 py-1">
            <span className="text-sm font-medium">Membros</span>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={selectAllMembers}
              >
                Selecionar todos
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={clearMembers}
                disabled={selectedCount === 0}
              >
                Limpar
              </Button>
            </div>
          </div>
          <div className="border-t pt-2 space-y-1 max-h-56 overflow-y-auto">
            {members.map((member) => {
              const checked = selectedMemberIds.has(member.id);
              const color = memberAreaColor(member.colorIndex, "dark");
              const isLastSelected = checked && selectedCount === 1;

              return (
                <div
                  key={member.id}
                  className={cn(
                    "flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted",
                    isLastSelected && "opacity-70",
                  )}
                  onClick={() => toggleMember(member.id, !checked)}
                >
                  <Checkbox
                    checked={checked}
                    disabled={isLastSelected}
                    onCheckedChange={(value) => toggleMember(member.id, value === true)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span
                    className="h-2.5 w-2.5 rounded-full shrink-0"
                    style={{ backgroundColor: color }}
                  />
                  <span className="text-sm truncate flex-1">{member.name}</span>
                </div>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>

      {!hasActivity ? (
        <div className="h-[280px] flex items-center justify-center">
          <p className="text-sm text-muted-foreground">
            Nenhuma conclusão no período para os membros selecionados
          </p>
        </div>
      ) : (
        <ChartContainer config={chartConfig} className="w-full aspect-auto h-[280px]">
          <AreaChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <defs>
              {visibleMembers.map((member) => {
                const color = memberAreaColor(member.colorIndex, "dark");
                const gradientId = `area-${member.dataKey}`;
                return (
                  <linearGradient key={gradientId} id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={color} stopOpacity={0.35} />
                    <stop offset="95%" stopColor={color} stopOpacity={0.04} />
                  </linearGradient>
                );
              })}
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              className="stroke-border/50"
            />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
              interval="preserveStartEnd"
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
              domain={[0, Math.max(2, maxValue + 1)]}
              width={32}
            />
            <ChartTooltip
              content={
                <AreaTooltip members={visibleMembers} metric={metric} />
              }
            />
            {visibleMembers.map((member) => {
              const color = memberAreaColor(member.colorIndex, "dark");
              return (
                <Area
                  key={member.dataKey}
                  type="monotone"
                  dataKey={member.dataKey}
                  name={member.name}
                  stroke={color}
                  strokeWidth={2}
                  fill={`url(#area-${member.dataKey})`}
                  dot={false}
                  activeDot={{ r: 4, strokeWidth: 0 }}
                />
              );
            })}
          </AreaChart>
        </ChartContainer>
      )}
    </div>
  );
}
